import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';

interface SimulatedModePromptModalProps {
  open: boolean;
  /** Turn on simulated mode and close the modal. */
  onEnable: () => void;
  /** Keep the builder in normal (non-simulated) mode and close the modal. */
  onDismiss: () => void;
}

/**
 * Shown when a customer with zero connected integrations lands on the
 * workflow builder. Offers to switch on the simulated environment so they
 * can build and test workflows without connecting anything first.
 * Chrome mirrors TestSignalModal.
 */
export function SimulatedModePromptModal({ open, onEnable, onDismiss }: SimulatedModePromptModalProps) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={onDismiss}>
      <div
        className="bg-background border rounded-lg shadow-xl max-w-lg w-full m-4"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="simulated-prompt-title"
      >
        <div className="px-5 py-4 border-b">
          <h3 id="simulated-prompt-title" className="text-base font-semibold">
            No integrations connected
          </h3>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Turn on the simulated environment to build and test workflows as if every
            integration were connected. Workflows saved in simulated mode are marked as
            simulated. Use the Test button to run them with mocked outputs instead of
            real integration actions.
          </p>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <Button variant="outline" size="sm" onClick={onDismiss}>
            Continue without simulated mode
          </Button>
          <Button size="sm" onClick={onEnable}>
            Enable simulated mode
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
